import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check, X, RotateCcw } from "lucide-react";

const CropInterface = ({ image, onComplete, onCancel }) => {
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragMode, setDragMode] = useState(""); // 'move', 'resize', 'new'
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBoundingBox, setShowBoundingBox] = useState(true);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const handlersRef = useRef({});

  // Use refs to store the latest handler functions to avoid stale closures
  const stateRef = useRef();
  stateRef.current = { isDragging, dragMode, dragStart, imageSize };

  // Document-level mouse handlers for resize
  const handleDocumentMouseMove = useCallback((e) => {
    const state = stateRef.current;
    if (!state.isDragging || !state.dragStart) return;

    const imageElement = document.querySelector('[alt="Screenshot to crop"]');
    if (!imageElement) return;

    const rect = imageElement.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    if (state.dragMode === "resize" && state.dragStart.originalCrop) {
      // Calculate the delta from where we started dragging
      const deltaX = currentX - state.dragStart.x;
      const deltaY = currentY - state.dragStart.y;

      // Get the original crop area when resize started
      const original = state.dragStart.originalCrop;
      let newBox = { ...original };

      switch (state.dragStart.handle) {
        case "nw":
          // Northwest: moving top-left corner
          newBox.x = original.x + deltaX;
          newBox.y = original.y + deltaY;
          newBox.width = original.width - deltaX;
          newBox.height = original.height - deltaY;
          break;
        case "ne":
          // Northeast: moving top-right corner
          newBox.y = original.y + deltaY;
          newBox.width = original.width + deltaX;
          newBox.height = original.height - deltaY;
          break;
        case "sw":
          // Southwest: moving bottom-left corner
          newBox.x = original.x + deltaX;
          newBox.width = original.width - deltaX;
          newBox.height = original.height + deltaY;
          break;
        case "se":
          // Southeast: moving bottom-right corner
          newBox.width = original.width + deltaX;
          newBox.height = original.height + deltaY;
          break;
        case "n":
          // North: moving top edge
          newBox.y = original.y + deltaY;
          newBox.height = original.height - deltaY;
          break;
        case "e":
          // East: moving right edge
          newBox.width = original.width + deltaX;
          break;
        case "s":
          // South: moving bottom edge
          newBox.height = original.height + deltaY;
          break;
        case "w":
          // West: moving left edge
          newBox.x = original.x + deltaX;
          newBox.width = original.width - deltaX;
          break;
      }

      // Enforce minimum size
      if (newBox.width < 20) {
        if (state.dragStart.handle.includes("w") || state.dragStart.handle === "w") {
          newBox.x = original.x + original.width - 20;
        }
        newBox.width = 20;
      }
      if (newBox.height < 20) {
        if (state.dragStart.handle.includes("n") || state.dragStart.handle === "n") {
          newBox.y = original.y + original.height - 20;
        }
        newBox.height = 20;
      }

      // Enforce image bounds
      newBox.x = Math.max(
        0,
        Math.min(newBox.x, state.imageSize.width - newBox.width)
      );
      newBox.y = Math.max(
        0,
        Math.min(newBox.y, state.imageSize.height - newBox.height)
      );
      newBox.width = Math.min(newBox.width, state.imageSize.width - newBox.x);
      newBox.height = Math.min(
        newBox.height,
        state.imageSize.height - newBox.y
      );

      setCropArea(newBox);
    }
  }, []);

  const handleDocumentMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragMode("");
    setDragStart(null);
    document.removeEventListener("mousemove", handlersRef.current.mouseMove);
    document.removeEventListener("mouseup", handlersRef.current.mouseUp);
  }, []);

  // Store handlers in ref to ensure latest versions
  handlersRef.current.mouseMove = handleDocumentMouseMove;
  handlersRef.current.mouseUp = handleDocumentMouseUp;

  const updateImageSize = useCallback((img) => {
    // Get the actual rendered size of the image element (what user sees)
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    setImageSize({ width: displayWidth, height: displayHeight });
    return { displayWidth, displayHeight };
  }, []);

  const handleImageLoad = useCallback((img) => {
    const { displayWidth, displayHeight } = updateImageSize(img);

    // Set crop area to 90% of the displayed image dimensions
    const defaultCropWidth = displayWidth * 0.9;
    const defaultCropHeight = displayHeight * 0.9;
    setCropArea({
      x: (displayWidth - defaultCropWidth) / 2,
      y: (displayHeight - defaultCropHeight) / 2,
      width: defaultCropWidth,
      height: defaultCropHeight,
    });

    // Set up resize observer to update size when image is resized
    const resizeObserver = new ResizeObserver(() => {
      // Force re-calculation of image size and adjust crop area proportionally
      requestAnimationFrame(() => {
        const oldSize = { ...imageSize };
        const { displayWidth: newWidth, displayHeight: newHeight } = updateImageSize(img);

        // If image size changed, adjust crop area proportionally
        if (oldSize.width > 0 && oldSize.height > 0 && (oldSize.width !== newWidth || oldSize.height !== newHeight)) {
          setCropArea(prevCrop => {
            const scaleX = newWidth / oldSize.width;
            const scaleY = newHeight / oldSize.height;

            return {
              x: prevCrop.x * scaleX,
              y: prevCrop.y * scaleY,
              width: prevCrop.width * scaleX,
              height: prevCrop.height * scaleY,
            };
          });
        }
      });
    });
    resizeObserver.observe(img);

    // Store observer for cleanup
    img._resizeObserver = resizeObserver;
  }, [updateImageSize, imageSize]);

  const getResizeHandle = (x, y) => {
    const handleSize = 16;
    const handles = [
      // Corner handles
      { x: cropArea.x, y: cropArea.y, handle: "nw" },
      { x: cropArea.x + cropArea.width, y: cropArea.y, handle: "ne" },
      { x: cropArea.x, y: cropArea.y + cropArea.height, handle: "sw" },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height, handle: "se" },
      // Middle edge handles
      { x: cropArea.x + cropArea.width / 2, y: cropArea.y, handle: "n" },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height / 2, handle: "e" },
      { x: cropArea.x + cropArea.width / 2, y: cropArea.y + cropArea.height, handle: "s" },
      { x: cropArea.x, y: cropArea.y + cropArea.height / 2, handle: "w" },
    ];

    for (const h of handles) {
      if (
        x >= h.x - handleSize / 2 &&
        x <= h.x + handleSize / 2 &&
        y >= h.y - handleSize / 2 &&
        y <= h.y + handleSize / 2
      ) {
        return h.handle;
      }
    }
    return null;
  };

  const getCursor = (e) => {
    if (isProcessing) return "wait";
    if (isDragging) {
      if (dragMode === "move") return "grabbing";
      if (dragMode === "resize") {
        // Map edge handles to appropriate cursor styles
        switch (dragStart.handle) {
          case "n":
          case "s":
            return "ns-resize";
          case "e":
          case "w":
            return "ew-resize";
          default:
            return `${dragStart.handle}-resize`;
        }
      }
      return "crosshair";
    }

    if (e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const resizeHandle = getResizeHandle(x, y);
      if (resizeHandle) {
        // Map edge handles to appropriate cursor styles
        switch (resizeHandle) {
          case "n":
          case "s":
            return "ns-resize";
          case "e":
          case "w":
            return "ew-resize";
          default:
            return `${resizeHandle}-resize`;
        }
      }

      const isInsideCropArea =
        x >= cropArea.x &&
        x <= cropArea.x + cropArea.width &&
        y >= cropArea.y &&
        y <= cropArea.y + cropArea.height &&
        cropArea.width > 0 &&
        cropArea.height > 0;

      if (isInsideCropArea) return "grab";
    }

    return "crosshair";
  };

  const handleMouseDown = (e) => {
    if (isProcessing) return; // Don't allow interaction while processing

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on resize handles first
    const resizeHandle = getResizeHandle(x, y);
    if (resizeHandle && cropArea.width > 0 && cropArea.height > 0) {
      setIsDragging(true);
      setDragMode("resize");
      setDragStart({
        x,
        y,
        handle: resizeHandle,
        originalCrop: { ...cropArea }, // Store original crop area for accurate delta calculation
      });
      e.preventDefault();
      // Add global listeners for resize
      document.addEventListener("mousemove", handlersRef.current.mouseMove);
      document.addEventListener("mouseup", handlersRef.current.mouseUp);
      return;
    }

    // Check if clicking inside existing crop area
    const isInsideCropArea =
      x >= cropArea.x &&
      x <= cropArea.x + cropArea.width &&
      y >= cropArea.y &&
      y <= cropArea.y + cropArea.height &&
      cropArea.width > 0 &&
      cropArea.height > 0;

    if (isInsideCropArea) {
      // Start moving the crop area
      setIsDragging(true);
      setDragMode("move");
      setDragStart({
        x: x - cropArea.x,
        y: y - cropArea.y,
      });
    } else {
      // Start new selection
      setIsDragging(true);
      setDragMode("new");
      setDragStart({ x, y });
      setCropArea({ x, y, width: 0, height: 0 });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    if (dragMode === "move") {
      // Move the existing crop area
      const newX = Math.max(
        0,
        Math.min(currentX - dragStart.x, imageSize.width - cropArea.width)
      );
      const newY = Math.max(
        0,
        Math.min(currentY - dragStart.y, imageSize.height - cropArea.height)
      );

      setCropArea((prev) => ({
        ...prev,
        x: newX,
        y: newY,
      }));
    } else if (dragMode === "resize") {
      // Skip resize in this handler - it's handled by document-level handler
      return;
    } else if (dragMode === "new") {
      // Create new crop area
      const width = Math.abs(currentX - dragStart.x);
      const height = Math.abs(currentY - dragStart.y);
      const x = Math.min(currentX, dragStart.x);
      const y = Math.min(currentY, dragStart.y);

      setCropArea({
        x: Math.max(0, Math.min(x, imageSize.width - width)),
        y: Math.max(0, Math.min(y, imageSize.height - height)),
        width: Math.min(width, imageSize.width - x),
        height: Math.min(height, imageSize.height - y),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragMode("");
    setDragStart(null);
  };

  const handleCropConfirm = () => {
    if (cropArea.width === 0 || cropArea.height === 0) return;

    setIsProcessing(true);

    // Hide the bounding box first
    setShowBoundingBox(false);

    // Wait for the UI to update and bounding box to disappear
    setTimeout(() => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const scaleX = img.naturalWidth / imageSize.width;
        const scaleY = img.naturalHeight / imageSize.height;

        const sourceX = cropArea.x * scaleX;
        const sourceY = cropArea.y * scaleY;
        const sourceWidth = cropArea.width * scaleX;
        const sourceHeight = cropArea.height * scaleY;

        canvas.width = sourceWidth;
        canvas.height = sourceHeight;

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          sourceWidth,
          sourceHeight
        );

        const croppedDataUrl = canvas.toDataURL("image/png");
        onComplete(croppedDataUrl);
      };

      img.src = image;
    }, 200); // 200ms delay to ensure bounding box is hidden
  };

  const resetCrop = () => {
    // Reset to 90% of the image dimensions
    const defaultCropWidth = imageSize.width * 0.9;
    const defaultCropHeight = imageSize.height * 0.9;
    setCropArea({
      x: (imageSize.width - defaultCropWidth) / 2,
      y: (imageSize.height - defaultCropHeight) / 2,
      width: defaultCropWidth,
      height: defaultCropHeight,
    });
  };

  // Handle window resize to maintain crop box proportions
  useEffect(() => {
    let resizeTimeout;
    const handleWindowResize = () => {
      // Throttle resize events for better performance
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const img = document.querySelector('[alt="Screenshot to crop"]');
        if (img && img.complete) {
          const oldSize = { ...imageSize };
          const newWidth = img.clientWidth;
          const newHeight = img.clientHeight;

          // If image size changed due to window resize, adjust crop area proportionally
          if (oldSize.width > 0 && oldSize.height > 0 && (oldSize.width !== newWidth || oldSize.height !== newHeight)) {
            const scaleX = newWidth / oldSize.width;
            const scaleY = newHeight / oldSize.height;

            setCropArea(prevCrop => ({
              x: prevCrop.x * scaleX,
              y: prevCrop.y * scaleY,
              width: prevCrop.width * scaleX,
              height: prevCrop.height * scaleY,
            }));

            setImageSize({ width: newWidth, height: newHeight });
          }
        }
      }, 100); // 100ms throttle
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(resizeTimeout);
    };
  }, [imageSize]);

  // Force image size recalculation when crop area changes
  useEffect(() => {
    const img = document.querySelector('[alt="Screenshot to crop"]');
    if (img && img.complete) {
      // Recalculate size to ensure overlay matches exactly
      const displayWidth = img.clientWidth;
      const displayHeight = img.clientHeight;

      if (displayWidth !== imageSize.width || displayHeight !== imageSize.height) {
        setImageSize({ width: displayWidth, height: displayHeight });
      }
    }
  }, [cropArea, imageSize.width, imageSize.height]);

  // Cleanup event listeners and resize observer on unmount
  useEffect(() => {
    return () => {
      if (handlersRef.current.mouseMove) {
        document.removeEventListener(
          "mousemove",
          handlersRef.current.mouseMove
        );
      }
      if (handlersRef.current.mouseUp) {
        document.removeEventListener("mouseup", handlersRef.current.mouseUp);
      }

      // Cleanup resize observer
      const img = document.querySelector('[alt="Screenshot to crop"]');
      if (img && img._resizeObserver) {
        img._resizeObserver.disconnect();
        delete img._resizeObserver;
      }
    };
  }, []);

  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex flex-col items-center justify-center z-[99998] p-6">
      <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-6 max-w-7xl w-full max-h-[95vh] flex flex-col border border-gray-200/50">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              Select Area to Capture
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Click and drag to adjust the selection area
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetCrop}
              className="group p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
              title="Reset selection">
              <RotateCcw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <button
              onClick={onCancel}
              className="group p-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200">
              <X className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center relative min-h-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 overflow-hidden">
          <div
            className="relative inline-block rounded-lg overflow-hidden shadow-lg max-w-full max-h-full"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}>
            <img
              src={image}
              alt="Screenshot to crop"
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{
                maxWidth: "calc(100vw - 12rem)", // Account for dialog padding
                maxHeight: "calc(100vh - 20rem)", // Account for header and footer
                cursor: getCursor(),
              }}
              onLoad={(e) => handleImageLoad(e.target)}
              onMouseDown={handleMouseDown}
              onMouseMove={(e) => {
                handleMouseMove(e);
                // Force re-render to update cursor
                e.currentTarget.style.cursor = getCursor(e);
              }}
              onMouseUp={handleMouseUp}
              draggable={false}
            />

            {imageSize.width > 0 && showBoundingBox && !isProcessing && (
              <>
                {/* Crop selection box */}
                <div
                  className="absolute border-2 border-blue-500 rounded-lg"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.width,
                    height: cropArea.height,
                    pointerEvents: "none",
                    boxShadow:
                      "0 0 0 1px rgba(59, 130, 246, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2)",
                  }}
                />

                {/* Resize handles */}
                {cropArea.width > 0 && cropArea.height > 0 && (
                  <>
                    {/* Edge lines for visual guidance */}
                    <div
                      className="absolute border-l-2 border-blue-400 opacity-50"
                      style={{
                        left: cropArea.x,
                        top: cropArea.y,
                        height: cropArea.height,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      className="absolute border-r-2 border-blue-400 opacity-50"
                      style={{
                        left: cropArea.x + cropArea.width - 2,
                        top: cropArea.y,
                        height: cropArea.height,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      className="absolute border-t-2 border-blue-400 opacity-50"
                      style={{
                        left: cropArea.x,
                        top: cropArea.y,
                        width: cropArea.width,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      className="absolute border-b-2 border-blue-400 opacity-50"
                      style={{
                        left: cropArea.x,
                        top: cropArea.y + cropArea.height - 2,
                        width: cropArea.width,
                        pointerEvents: "none",
                      }}
                    />

                    {/* Corner handles */}
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-nw-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x - 8,
                        top: cropArea.y - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "nw",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-ne-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x + cropArea.width - 8,
                        top: cropArea.y - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "ne",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-sw-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x - 8,
                        top: cropArea.y + cropArea.height - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "sw",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-se-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x + cropArea.width - 8,
                        top: cropArea.y + cropArea.height - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "se",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />

                    {/* Middle edge handles */}
                    {/* North handle */}
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-ns-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x + cropArea.width / 2 - 8,
                        top: cropArea.y - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "n",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />

                    {/* East handle */}
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-ew-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x + cropArea.width - 8,
                        top: cropArea.y + cropArea.height / 2 - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "e",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />

                    {/* South handle */}
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-ns-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x + cropArea.width / 2 - 8,
                        top: cropArea.y + cropArea.height - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "s",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />

                    {/* West handle */}
                    <div
                      className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg cursor-ew-resize z-10 hover:scale-125 transition-transform"
                      style={{
                        left: cropArea.x - 8,
                        top: cropArea.y + cropArea.height / 2 - 8,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const rect = e.currentTarget.parentElement
                          .querySelector("img")
                          .getBoundingClientRect();
                        setIsDragging(true);
                        setDragMode("resize");
                        setDragStart({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          handle: "w",
                          originalCrop: { ...cropArea },
                        });
                        document.addEventListener(
                          "mousemove",
                          handlersRef.current.mouseMove
                        );
                        document.addEventListener(
                          "mouseup",
                          handlersRef.current.mouseUp
                        );
                      }}
                    />
                  </>
                )}

                {/* Overlay that covers the entire image area */}
                <div
                  className="absolute bg-black bg-opacity-50 pointer-events-none"
                  style={{
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    clipPath: `polygon(0 0, 0 100%, ${cropArea.x}px 100%, ${
                      cropArea.x
                    }px ${cropArea.y}px, ${cropArea.x + cropArea.width}px ${
                      cropArea.y
                    }px, ${cropArea.x + cropArea.width}px ${
                      cropArea.y + cropArea.height
                    }px, ${cropArea.x}px ${cropArea.y + cropArea.height}px, ${
                      cropArea.x
                    }px 100%, 100% 100%, 100% 0)`,
                  }}
                />
              </>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-6">
          <div className="flex justify-center gap-4">
            <button
              onClick={onCancel}
              className="group px-8 py-3.5 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center gap-3 font-medium shadow-sm hover:shadow-md">
              <X className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>Cancel</span>
            </button>

            <button
              onClick={handleCropConfirm}
              disabled={
                cropArea.width === 0 || cropArea.height === 0 || isProcessing
              }
              className="group px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-3 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:shadow-sm">
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>Crop & Continue</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CropInterface;
